(() => {
  const fold = (value) => String(value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');

  const compact = (value) => fold(value).replace(/\s/g, '');
  const tokenize = (value) => fold(value).split(' ').filter(Boolean);
  const unique = (values) => [...new Set(values.filter(Boolean))];

  const editDistanceAtMostOne = (left, right) => {
    if (left === right) return 0;
    if (Math.abs(left.length - right.length) > 1) return 2;
    let leftIndex = 0;
    let rightIndex = 0;
    let edits = 0;

    while (leftIndex < left.length && rightIndex < right.length) {
      if (left[leftIndex] === right[rightIndex]) {
        leftIndex += 1;
        rightIndex += 1;
        continue;
      }
      edits += 1;
      if (edits > 1) return 2;
      if (left.length > right.length) leftIndex += 1;
      else if (right.length > left.length) rightIndex += 1;
      else {
        leftIndex += 1;
        rightIndex += 1;
      }
    }

    if (leftIndex < left.length || rightIndex < right.length) edits += 1;
    return edits;
  };

  const bestTokenMatch = (queryToken, primaryTokens, secondaryTokens) => {
    if (primaryTokens.includes(queryToken)) return 1;
    if (secondaryTokens.includes(queryToken)) return 0.9;

    const allTokens = unique([...primaryTokens, ...secondaryTokens]);
    if (queryToken.length >= 3 && allTokens.some((token) => token.startsWith(queryToken))) return 0.82;

    if (queryToken.length >= 5 && !/\d/.test(queryToken)) {
      if (allTokens.some((token) => editDistanceAtMostOne(queryToken, token) === 1)) return 0.72;
    }
    return 0;
  };

  const scoreDocument = (document, rawQuery) => {
    const machineQuery = globalThis.RicochetSearchNormalizer?.normalizeSearchQuery?.(rawQuery)
      || globalThis.RicochetSearchNormalizer?.normalizeQuery(rawQuery) || rawQuery;
    const normalizedQuery = globalThis.RicochetSearchNormalizer?.normalizeProductQuery?.(machineQuery) || machineQuery;
    const query = fold(normalizedQuery);
    const queryCompact = compact(normalizedQuery);
    const queryTokens = unique(tokenize(normalizedQuery));
    if (!queryTokens.length) return 0;

    const identityFields = [document.t, document.vh, document.mk, document.md, document.sm, document.pt];
    const secondaryFields = [
      document.v,
      document.bt,
      document.mt,
      document.c1,
      document.c2,
      ...(document.vt || []),
      ...(document.tg || []),
    ];
    const skuCompacts = (document.sk || []).map(compact);
    const identityFolded = identityFields.map(fold).filter(Boolean);
    const identityCompacts = identityFields.map(compact).filter(Boolean);
    const primaryTokens = unique(identityFields.flatMap(tokenize));
    const secondaryTokens = unique(secondaryFields.flatMap(tokenize));
    const combined = fold([...identityFields, ...secondaryFields].filter(Boolean).join(' '));

    if (skuCompacts.includes(queryCompact)) return 100;
    if (fold(document.t) === query) return 100;
    if ([document.vh, document.md].some((value) => compact(value) === queryCompact)) return 98;
    if (compact(document.pt) === queryCompact) return 96;
    if (compact(document.mk) === queryCompact) return 94;

    const numericTokens = queryTokens.filter((token) => /\d/.test(token));
    if (numericTokens.some((token) => !combined.replace(/\s/g, '').includes(token.replace(/\s/g, '')))) return 0;

    const tokenScores = queryTokens.map((token) => bestTokenMatch(token, primaryTokens, secondaryTokens));
    const coverage = tokenScores.reduce((sum, score) => sum + score, 0) / queryTokens.length;
    if (coverage < 0.45) return 0;

    let score = 30 + coverage * 56;
    if (identityFolded.some((field) => field.includes(query))) score += 8;
    else if (combined.includes(query)) score += 4;
    if (identityCompacts.some((field) => field.includes(queryCompact))) score += 5;

    return Math.max(1, Math.min(97, Math.round(score)));
  };

  const rankDocuments = (documents, query) => documents
    .map((document) => ({ document, confidence: scoreDocument(document, query) }))
    .filter(({ confidence }) => confidence > 0)
    .sort((left, right) => (
      right.confidence - left.confidence
      || Number(Boolean(right.document.a)) - Number(Boolean(left.document.a))
      || String(left.document.t).localeCompare(String(right.document.t))
    ));

  const vehicleTypeDetails = (value) => {
    const key = compact(value);
    if (key === 'sidebyside' || key === 'utv') return { key: 'utv-sxs', label: 'UTV / SXS', order: 3 };
    if (key === 'motorcycle') return { key: 'motorcycle', label: 'Motorcycle', order: 1 };
    if (key === 'atv') return { key: 'atv', label: 'ATV', order: 2 };
    if (key === 'automotive') return { key: 'automotive', label: 'Automotive', order: 4 };
    return { key: key || 'other', label: value || 'Other', order: 99 };
  };

  const vehicleKey = (product) => compact([
    vehicleTypeDetails(product.c1).key,
    product.mk,
    product.md,
    product.sm,
  ].filter(Boolean).join(' '));

  const vehicleLabel = (product) => {
    const supplied = product.vh || [product.mk, product.md, product.sm].filter(Boolean).join(' ');
    if (!product.sm || compact(supplied).includes(compact(product.sm))) return supplied;
    return `${supplied} ${product.sm}`;
  };

  const vehicleCandidates = (product) => unique([
    product.md,
    product.vh,
    [product.mk, product.md].filter(Boolean).join(' '),
    [product.md, product.sm].filter(Boolean).join(' '),
    [product.vh, product.sm].filter(Boolean).join(' '),
    [product.mk, product.md, product.sm].filter(Boolean).join(' '),
  ]);

  const partTitle = (product) => {
    const vehicleValues = [product.mk, product.md, product.sm, product.vh].map(compact).filter(Boolean);
    const parts = String(product.t || '').split(/\s+\|\s+/).filter(Boolean);
    const kept = parts.filter((part) => {
      const candidate = compact(part);
      return !vehicleValues.some((vehicle) => candidate === vehicle || candidate.includes(vehicle));
    });
    return kept.join(' | ') || product.t;
  };

  const commonEdgeLengths = (values) => {
    if (!values.length) return { prefix: 0, suffix: 0 };
    const folded = values.map((value) => String(value).toLowerCase());
    let prefix = 0;
    while (folded.every((value) => value[prefix] && value[prefix] === folded[0][prefix])) prefix += 1;
    let suffix = 0;
    while (
      folded.every((value) => value.length - suffix - 1 >= prefix)
      && folded.every((value) => value[value.length - suffix - 1] === folded[0][folded[0].length - suffix - 1])
    ) suffix += 1;
    return prefix >= 2 ? { prefix, suffix } : { prefix: 0, suffix: 0 };
  };

  const modelFamilyKey = (model) => {
    const value = String(model || '').toUpperCase().replace(/[^A-Z0-9]+/g, '');
    const vehicleCode = value.match(/^([A-Z]+)(\d+)/);
    if (vehicleCode) return `${vehicleCode[1]}${vehicleCode[2]}`;
    const words = fold(model).split(' ').filter(Boolean);
    return compact(words.slice(0, 2).join(' ')) || value;
  };

  const appendComparedModel = (element, model, edges, baseLabel = ' Standard') => {
    const prefix = model.slice(0, edges.prefix);
    const suffix = edges.suffix ? model.slice(model.length - edges.suffix) : '';
    const difference = model.slice(edges.prefix, model.length - edges.suffix || undefined);
    if (prefix) element.append(document.createTextNode(prefix));
    const emphasized = document.createElement('strong');
    emphasized.textContent = difference || baseLabel;
    element.append(emphasized);
    if (suffix) element.append(document.createTextNode(suffix));
  };

  globalThis.RicochetSearchRanking = Object.freeze({ fold, compact, scoreDocument, rankDocuments });
  if (typeof customElements === 'undefined' || customElements.get('ricochet-search-results')) return;

  class RicochetSearchResults extends HTMLElement {
    connectedCallback() {
      if (this.dataset.collectionSearchEnabled === 'true' && globalThis.RicochetCollectionSearch
          && Array.isArray(globalThis.RicochetSearchData?.collections)) {
        this.connectCollectionSearch();
        return;
      }
      const index = globalThis.RicochetSearchIndex;
      if (!index?.documents?.length) return;

      const urlParameters = new URLSearchParams(window.location.search);
      this.documents = index.documents;
      this.query = urlParameters.get('original_q') || this.dataset.query || '';
      this.normalizedQuery = globalThis.RicochetSearchNormalizer?.normalizeQuery(this.query) || this.query;
      this.pageSize = Number(this.dataset.pageSize) || 20;
      this.visibleCount = this.pageSize;
      this.currency = this.dataset.currency || 'USD';
      this.matches = rankDocuments(this.documents, this.query);
      this.vehicleMode = this.dataset.machineSelectorEnabled === 'true' && this.hasVehicleIntent();
      this.suggestedVehicles = this.groupVehicles(this.matches);
      this.catalogVehicles = this.groupVehicles(
        this.documents.map((document) => ({ document, confidence: 0 }))
      );
      this.catalogVehicleMap = new Map(this.catalogVehicles.map((group) => [group.key, group]));

      this.resultsElement = this.querySelector('[data-search-results]');
      this.statusElement = this.querySelector('[data-search-status]');
      this.headingElement = this.querySelector('[data-search-heading]');
      this.normalizedElement = this.querySelector('[data-normalized-query]');
      this.machineElement = this.querySelector('[data-search-machine]');
      this.typeSelect = this.querySelector('[data-search-machine-type]');
      this.makeSelect = this.querySelector('[data-search-machine-make]');
      this.modelSelect = this.querySelector('[data-search-machine-model]');
      this.typePickerElement = this.querySelector('[data-search-type-picker]');
      this.makePickerElement = this.querySelector('[data-search-make-picker]');
      this.modelPickerElement = this.querySelector('[data-search-model-picker]');
      this.suggestionsElement = this.querySelector('[data-search-machine-suggestions]');
      this.showMoreButton = this.querySelector('[data-search-show-more]');
      this.standardButton = this.querySelector('[data-search-standard]');

      const requestedVehicle = urlParameters.get('fitment_vehicle') || '';
      const requestedType = urlParameters.get('fitment_vehicle_type') || '';
      const requestedMake = urlParameters.get('fitment_make') || '';
      this.selectionWasRequested = this.catalogVehicleMap.has(requestedVehicle);
      const normalizedVehicle = compact(this.normalizedQuery);
      const exactSuggestion = this.suggestedVehicles.find((group) => (
        vehicleCandidates(group.matches[0].document).some((candidate) => compact(candidate) === normalizedVehicle)
      ));
      const bestConfidence = this.suggestedVehicles[0]?.confidence;
      const equallyLikely = this.suggestedVehicles.filter((group) => group.confidence === bestConfidence);
      const inferredVehicle = exactSuggestion || (equallyLikely.length === 1 ? equallyLikely[0] : null);
      this.selectedVehicle = this.vehicleMode
        ? (this.selectionWasRequested ? requestedVehicle : (inferredVehicle?.key || ''))
        : '';
      const selectedGroup = this.catalogVehicleMap.get(this.selectedVehicle);
      this.selectedType = selectedGroup?.typeKey
        || this.catalogVehicles.find((group) => group.typeKey === requestedType)?.typeKey
        || this.suggestedVehicles[0]?.typeKey
        || '';
      this.selectedMake = selectedGroup?.make
        || this.catalogVehicles.find((group) => (
          group.typeKey === this.selectedType && compact(group.make) === compact(requestedMake)
        ))?.make
        || this.suggestedVehicles[0]?.make
        || '';

      this.configureMode();
      this.populateMachineSelectors();
      this.addEventListeners();
      this.render();
      this.hidden = false;
      const nativeResults = document.querySelector('[data-native-search-results]');
      if (nativeResults) nativeResults.hidden = true;
    }

    connectCollectionSearch() {
      const parameters = new URLSearchParams(window.location.search);
      this.query = parameters.get('original_q') || this.dataset.query || '';
      this.normalizedQuery = globalThis.RicochetSearchNormalizer?.normalizeQuery(this.query) || this.query;
      this.documents = globalThis.RicochetSearchIndex?.documents || [];
      this.matches = rankDocuments(this.documents, this.query);
      this.collectionMatches = globalThis.RicochetCollectionSearch.rank(globalThis.RicochetSearchData.collections, this.query);
      this.collectionMode = parameters.get('search_scope') !== 'products'
        && !globalThis.RicochetCollectionSearch.isProductQuery(this.query)
        && this.collectionMatches.length > 0;
      this.vehicleMode = false;
      this.pageSize = Math.max(1, Math.min(50, Number(this.dataset.pageSize) || 20));
      this.visibleCount = this.pageSize;
      this.currency = this.dataset.currency || 'USD';
      this.resultsElement = this.querySelector('[data-search-results]');
      this.statusElement = this.querySelector('[data-search-status]');
      this.headingElement = this.querySelector('[data-search-heading]');
      this.showMoreButton = this.querySelector('[data-search-show-more]');
      this.standardButton = this.querySelector('[data-search-standard]');
      this.productsButton = this.querySelector('[data-search-products]');
      const heading = document.querySelector('.ricochet-search-heading');
      if (heading) heading.textContent = `Search results for “${this.query}”`;
      this.showMoreButton?.addEventListener('click', () => {
        this.visibleCount += this.pageSize;
        this.renderCollectionSearch();
      });
      this.standardButton?.addEventListener('click', () => {
        this.hidden = true;
        const native = document.querySelector('[data-native-search-results]');
        if (native) native.hidden = false;
      });
      this.productsButton?.addEventListener('click', () => {
        this.collectionMode = !this.collectionMode;
        this.visibleCount = this.pageSize;
        this.renderCollectionSearch();
      });
      this.renderCollectionSearch();
    }

    createCollectionResult({ document: collection }) {
      const item = document.createElement('li');
      item.className = 'ricochet-collection-result';
      item.dataset.collectionResult = '';
      const url = `/collections/${encodeURIComponent(collection.h)}`;
      if (collection.im && /^https:\/\//.test(collection.im)) {
        const image = document.createElement('img');
        image.className = 'ricochet-collection-result__image';
        image.src = collection.im;
        image.alt = collection.ia || '';
        image.width = 72;
        image.height = 72;
        image.loading = 'lazy';
        item.append(image);
      }
      const content = document.createElement('div');
      content.className = 'ricochet-collection-result__content';
      const heading = document.createElement('h3');
      heading.className = 'h5';
      const link = document.createElement('a');
      link.href = url;
      link.textContent = collection.t;
      heading.append(link);
      const action = document.createElement('a');
      action.className = 'ricochet-collection-result__link link text-current';
      action.href = url;
      action.textContent = 'View parts →';
      action.setAttribute('aria-label', `View parts in ${collection.t}`);
      content.append(heading, action);
      item.append(content);
      return item;
    }

    renderCollectionSearch() {
      const matches = this.collectionMode ? this.collectionMatches : this.matches;
      const visible = matches.slice(0, this.visibleCount);
      const list = document.createElement('ol');
      list.className = 'ricochet-search-results-list';
      list.append(...visible.map((match) => this.collectionMode ? this.createCollectionResult(match) : this.createResult(match)));
      this.resultsElement.replaceChildren(list);
      this.headingElement.textContent = this.collectionMode ? 'Matching collections' : 'Product results';
      this.statusElement.textContent = this.collectionMode
        ? `${matches.length} matching ${matches.length === 1 ? 'collection' : 'collections'}. Choose yours to view parts.`
        : `${matches.length} product ${matches.length === 1 ? 'result' : 'results'} for “${this.query}”.`;
      const withoutYear = globalThis.RicochetSearchNormalizer?.stripModelYears?.(this.query) || this.query;
      if (withoutYear !== this.query) {
        this.statusElement.textContent += ' Year not filtered—check fitment years before ordering.';
      }
      if (this.productsButton) {
        this.productsButton.hidden = !this.collectionMatches.length;
        this.productsButton.textContent = this.collectionMode ? 'Products instead' : 'Show matching collections';
      }
      if (this.showMoreButton) {
        this.showMoreButton.hidden = visible.length >= matches.length;
        this.showMoreButton.textContent = `Show more (${matches.length - visible.length} remaining)`;
      }
      this.hidden = !matches.length;
      const native = document.querySelector('[data-native-search-results]');
      if (native) native.hidden = Boolean(matches.length);
    }

    hasVehicleIntent() {
      const query = compact(this.normalizedQuery);
      if (!query || !this.matches.length) return false;
      const exactProduct = this.matches.some(({ document, confidence }) => (
        confidence === 100
        && (fold(document.t) === fold(this.normalizedQuery)
          || (document.sk || []).some((sku) => compact(sku) === query))
      ));
      if (exactProduct) return false;
      if (this.matches.some(({ document }) => compact(document.pt) === query)) return false;
      if (this.matches.some(({ document, confidence }) => confidence >= 90 && compact(document.mk) === query)) return true;

      const exactVehicle = this.matches.some(({ document: product, confidence }) => (
        confidence >= 90 && vehicleCandidates(product).some((candidate) => compact(candidate) === query)
      ));
      if (exactVehicle) return true;

      const bestScore = this.matches[0].confidence;
      const nearbyVehicles = new Set(
        this.matches
          .filter(({ confidence }) => confidence >= Math.max(70, bestScore - 8))
          .map(({ document }) => vehicleKey(document))
          .filter(Boolean)
      );
      return bestScore >= 72 && nearbyVehicles.size > 0 && nearbyVehicles.size <= 8;
    }

    groupVehicles(matches) {
      const groups = new Map();
      matches.forEach((match) => {
        const product = match.document;
        const type = vehicleTypeDetails(product.c1);
        const key = vehicleKey(product);
        if (!key || type.key === 'other' || !product.mk || !product.md) return;
        if (!groups.has(key)) {
          const model = product.sm && !compact(product.md).includes(compact(product.sm))
            ? `${product.md} ${product.sm}`
            : product.md;
          groups.set(key, {
            key,
            typeKey: type.key,
            typeLabel: type.label,
            typeOrder: type.order,
            make: product.mk,
            model,
            label: vehicleLabel(product),
            matches: [],
            confidence: match.confidence,
          });
        }
        const group = groups.get(key);
        group.matches.push(match);
        group.confidence = Math.max(group.confidence, match.confidence);
      });
      return [...groups.values()].sort((left, right) => (
        right.confidence - left.confidence || left.label.localeCompare(right.label)
      ));
    }

    configureMode() {
      if (this.headingElement) this.headingElement.textContent = this.vehicleMode ? 'Choose your machine' : 'Product results';
      if (this.machineElement) this.machineElement.hidden = !this.vehicleMode;
    }

    machineOption(group) {
      const catalogGroup = this.catalogVehicleMap.get(group.key) || group;
      const count = catalogGroup.matches.length;
      return new Option(`${group.model} — ${count} ${count === 1 ? 'part' : 'parts'}`, group.key);
    }

    populateMachineSelectors() {
      if (!this.vehicleMode || !this.typeSelect || !this.makeSelect || !this.modelSelect) return;
      const typeMap = new Map();
      this.catalogVehicles.forEach((group) => {
        if (!typeMap.has(group.typeKey)) {
          typeMap.set(group.typeKey, { key: group.typeKey, label: group.typeLabel, order: group.typeOrder });
        }
      });
      const types = [...typeMap.values()].sort((left, right) => left.order - right.order || left.label.localeCompare(right.label));
      this.typeSelect.replaceChildren(...types.map((type) => new Option(type.label, type.key)));
      if (!typeMap.has(this.selectedType)) this.selectedType = types[0]?.key || '';
      this.typeSelect.value = this.selectedType;

      const makes = unique(
        this.catalogVehicles.filter((group) => group.typeKey === this.selectedType).map((group) => group.make)
      )
        .sort((left, right) => left.localeCompare(right));
      this.makeSelect.replaceChildren(new Option('Choose a make', ''), ...makes.map((make) => new Option(make, make)));
      if (!makes.includes(this.selectedMake)) this.selectedMake = '';
      this.makeSelect.value = this.selectedMake;

      this.renderMachinePicker({
        kind: 'type',
        pickerElement: this.typePickerElement,
        selectElement: this.typeSelect,
        selectedValue: this.selectedType,
        placeholder: 'Choose a vehicle type',
        sectionTitle: 'Vehicle types',
        listLabel: 'Vehicle types',
        options: types.map((type) => ({
          value: type.key,
          label: type.label,
          count: this.catalogVehicles.filter((group) => group.typeKey === type.key).length,
        })),
      });
      this.renderMachinePicker({
        kind: 'make',
        pickerElement: this.makePickerElement,
        selectElement: this.makeSelect,
        selectedValue: this.selectedMake,
        placeholder: 'Choose a make',
        sectionTitle: `Makes for ${this.typeSelect.selectedOptions[0]?.textContent || 'this vehicle type'}`,
        listLabel: 'Makes',
        searchable: makes.length > 8,
        options: makes.map((make) => ({
          value: make,
          label: make,
          count: this.catalogVehicles.filter((group) => group.typeKey === this.selectedType && group.make === make).length,
        })),
      });

      const suggested = this.suggestedVehicles
        .filter((group) => group.typeKey === this.selectedType && group.make === this.selectedMake)
        .slice(0, 8);
      const suggestedKeys = new Set(suggested.map((group) => group.key));
      const otherModels = this.catalogVehicles
        .filter((group) => (
          group.typeKey === this.selectedType
          && group.make === this.selectedMake
          && !suggestedKeys.has(group.key)
        ))
        .sort((left, right) => left.model.localeCompare(right.model));
      const children = [new Option('Choose a model', '')];
      if (suggested.length) {
        const suggestions = document.createElement('optgroup');
        suggestions.label = 'Suggested from your search';
        suggestions.append(...suggested.map((group) => this.machineOption(group)));
        children.push(suggestions);
      }
      if (otherModels.length) {
        const allModels = document.createElement('optgroup');
        allModels.label = `All ${this.selectedMake} models`;
        allModels.append(...otherModels.map((group) => this.machineOption(group)));
        children.push(allModels);
      }
      this.modelSelect.replaceChildren(...children);
      const selectedGroup = this.catalogVehicleMap.get(this.selectedVehicle);
      this.modelSelect.value = selectedGroup?.typeKey === this.selectedType && selectedGroup?.make === this.selectedMake
        ? this.selectedVehicle
        : '';
      this.renderSuggestions(suggested);
      this.renderModelPicker(suggested, [...suggested, ...otherModels]);
    }

    renderSuggestions(groups) {
      if (!this.suggestionsElement) return;
      this.suggestionsElement.hidden = groups.length < 2;
      if (groups.length < 2) {
        this.suggestionsElement.replaceChildren();
        return;
      }

      const heading = document.createElement('div');
      heading.className = 'ricochet-search-suggestions__heading';
      const title = document.createElement('p');
      title.textContent = 'Suggested from your search';
      const explanation = document.createElement('p');
      explanation.textContent = 'Key differences are bold.';
      heading.append(title, explanation);

      const list = document.createElement('div');
      list.className = 'ricochet-search-suggestions__list';
      const models = groups.map((group) => group.model);
      const edges = commonEdgeLengths(models);
      groups.forEach((group) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ricochet-search-suggestion${group.key === this.selectedVehicle ? ' is-selected' : ''}`;
        button.dataset.suggestedVehicle = group.key;
        button.setAttribute('aria-pressed', String(group.key === this.selectedVehicle));
        button.setAttribute('aria-label', `Select ${group.label}`);

        const model = document.createElement('span');
        model.className = 'ricochet-search-suggestion__model';
        appendComparedModel(model, group.model, edges);

        const catalogGroup = this.catalogVehicleMap.get(group.key) || group;
        const count = document.createElement('span');
        count.className = 'ricochet-search-suggestion__count';
        count.textContent = `${catalogGroup.matches.length} ${catalogGroup.matches.length === 1 ? 'part' : 'parts'}`;
        button.append(model, count);
        button.addEventListener('click', () => this.selectVehicle(group));
        list.append(button);
      });
      this.suggestionsElement.replaceChildren(heading, list);
    }

    selectVehicle(group) {
      this.selectionWasRequested = true;
      this.selectedVehicle = group.key;
      this.modelSelect.value = group.key;
      this.visibleCount = this.pageSize;
      this.populateMachineSelectors();
      this.updateUrl();
      this.render();
    }

    createModelPickerOption(group, edges = null) {
      const catalogGroup = this.catalogVehicleMap.get(group.key) || group;
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `ricochet-search-model-option${group.key === this.selectedVehicle ? ' is-selected' : ''}`;
      button.dataset.modelPickerOption = group.key;
      button.dataset.machinePickerOption = group.key;
      button.setAttribute('role', 'option');
      button.setAttribute('aria-selected', String(group.key === this.selectedVehicle));

      const model = document.createElement('span');
      model.className = 'ricochet-search-model-option__name';
      if (edges) appendComparedModel(model, group.model, edges);
      else model.textContent = group.model;
      const count = document.createElement('span');
      count.className = 'ricochet-search-model-option__count';
      count.textContent = `${catalogGroup.matches.length} ${catalogGroup.matches.length === 1 ? 'part' : 'parts'}`;
      button.append(model, count);
      button.addEventListener('click', () => this.selectVehicle(group));
      return button;
    }

    createModelPickerSection(titleText, groups, familyGroups = false) {
      const section = document.createElement('section');
      section.className = 'ricochet-search-model-section';
      const title = document.createElement('p');
      title.className = 'ricochet-search-model-section__title';
      title.textContent = titleText;
      section.append(title);

      if (!familyGroups) {
        const list = document.createElement('div');
        list.className = 'ricochet-search-model-family';
        const edges = groups.length > 1 ? commonEdgeLengths(groups.map((group) => group.model)) : null;
        list.append(...groups.map((group) => this.createModelPickerOption(group, edges)));
        section.append(list);
        return section;
      }

      const families = new Map();
      groups.forEach((group) => {
        const key = modelFamilyKey(group.model);
        if (!families.has(key)) families.set(key, []);
        families.get(key).push(group);
      });
      [...families.entries()]
        .sort((left, right) => left[1][0].model.localeCompare(right[1][0].model))
        .forEach(([key, family]) => {
          family.sort((left, right) => left.model.localeCompare(right.model));
          const list = document.createElement('div');
          list.className = 'ricochet-search-model-family';
          list.dataset.modelFamily = key.toLowerCase();
          const edges = family.length > 1 ? commonEdgeLengths(family.map((group) => group.model)) : null;
          list.append(...family.map((group) => this.createModelPickerOption(group, edges)));
          section.append(list);
        });
      return section;
    }

    machinePickerElements() {
      return [this.typePickerElement, this.makePickerElement, this.modelPickerElement].filter(Boolean);
    }

    closeMachinePickers() {
      this.machinePickerElements().forEach((pickerElement) => {
        const trigger = pickerElement.querySelector('[data-machine-picker-trigger]');
        const panel = pickerElement.querySelector('[data-machine-picker-panel]');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (panel) panel.hidden = true;
      });
    }

    fitMachinePickerToViewport(pickerElement) {
      const trigger = pickerElement?.querySelector('[data-machine-picker-trigger]');
      const panel = pickerElement?.querySelector('[data-machine-picker-panel]');
      if (!trigger || !panel || panel.hidden) return;
      const viewport = window.visualViewport;
      const viewportTop = viewport?.offsetTop || 0;
      const viewportBottom = viewportTop + (viewport?.height || window.innerHeight);
      const triggerRect = trigger.getBoundingClientRect();
      const spaceAbove = Math.max(0, triggerRect.top - viewportTop - 12);
      const spaceBelow = Math.max(0, viewportBottom - triggerRect.bottom - 12);
      const opensUp = spaceBelow < 420 && spaceAbove > spaceBelow;
      const availableSpace = opensUp ? spaceAbove : spaceBelow;
      panel.classList.toggle('opens-up', opensUp);
      panel.style.setProperty('--ricochet-model-panel-max-height', `${Math.max(120, Math.floor(availableSpace))}px`);
    }

    renderMachinePicker({
      kind,
      pickerElement,
      selectElement,
      selectedValue,
      placeholder,
      sectionTitle,
      listLabel,
      options,
      searchable = false,
    }) {
      if (!pickerElement || !selectElement) return;
      selectElement.hidden = true;
      const triggerId = `ricochet-search-${kind}-trigger`;
      const panelId = `ricochet-search-${kind}-panel`;
      const label = selectElement.closest('.ricochet-search-machine__field')?.querySelector('label');
      if (label) label.htmlFor = triggerId;

      const selectedOption = options.find((option) => option.value === selectedValue);
      const trigger = document.createElement('button');
      trigger.id = triggerId;
      trigger.type = 'button';
      trigger.className = 'ricochet-search-model-trigger';
      trigger.dataset.machinePickerTrigger = '';
      trigger.setAttribute(`data-${kind}-picker-trigger`, '');
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', panelId);
      trigger.textContent = selectedOption?.label || placeholder;

      const panel = document.createElement('div');
      panel.id = panelId;
      panel.className = 'ricochet-search-model-panel';
      panel.dataset.machinePickerPanel = '';
      panel.setAttribute(`data-${kind}-picker-panel`, '');
      panel.hidden = true;

      const list = document.createElement('div');
      list.className = 'ricochet-search-model-panel__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', listLabel);
      const section = document.createElement('section');
      section.className = 'ricochet-search-model-section';
      const title = document.createElement('p');
      title.className = 'ricochet-search-model-section__title';
      title.textContent = sectionTitle;
      const optionList = document.createElement('div');
      optionList.className = 'ricochet-search-model-family';
      options.forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `ricochet-search-model-option${option.value === selectedValue ? ' is-selected' : ''}`;
        button.dataset.machinePickerOption = option.value;
        button.setAttribute(`data-${kind}-picker-option`, option.value);
        button.setAttribute('role', 'option');
        button.setAttribute('aria-selected', String(option.value === selectedValue));
        const name = document.createElement('span');
        name.className = 'ricochet-search-model-option__name';
        name.textContent = option.label;
        const count = document.createElement('span');
        count.className = 'ricochet-search-model-option__count';
        count.textContent = `${option.count} ${option.count === 1 ? 'model' : 'models'}`;
        button.append(name, count);
        button.addEventListener('click', () => {
          selectElement.value = option.value;
          selectElement.dispatchEvent(new Event('change', { bubbles: true }));
        });
        optionList.append(button);
      });
      section.append(title, optionList);
      list.append(section);

      let search;
      let empty;
      if (searchable) {
        search = document.createElement('input');
        search.className = 'ricochet-search-model-panel__search';
        search.type = 'search';
        search.placeholder = `Search ${listLabel.toLowerCase()}`;
        search.setAttribute('aria-label', `Search ${listLabel.toLowerCase()}`);
        empty = document.createElement('p');
        empty.className = 'ricochet-search-model-panel__empty';
        empty.textContent = `No matching ${listLabel.toLowerCase()}. Try a shorter search.`;
        empty.hidden = true;
        list.append(empty);
        panel.append(search, list);
        search.addEventListener('input', () => {
          const query = fold(search.value);
          const optionButtons = [...list.querySelectorAll('[data-machine-picker-option]')];
          optionButtons.forEach((option) => {
            option.hidden = Boolean(query) && !fold(option.textContent).includes(query);
          });
          empty.hidden = optionButtons.some((option) => !option.hidden);
        });
      } else {
        panel.append(list);
      }

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const opening = panel.hidden;
        this.closeMachinePickers();
        panel.hidden = !opening;
        trigger.setAttribute('aria-expanded', String(opening));
        if (opening) {
          this.fitMachinePickerToViewport(pickerElement);
          (search || list.querySelector('[data-machine-picker-option]'))?.focus();
        }
      });
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeMachinePickers();
          trigger.focus();
        } else if (event.key === 'ArrowDown' && event.target === search) {
          event.preventDefault();
          list.querySelector('[data-machine-picker-option]:not([hidden])')?.focus();
        }
      });
      pickerElement.replaceChildren(trigger, panel);
    }

    renderModelPicker(suggested, allModels) {
      if (!this.modelPickerElement || !this.modelSelect) return;
      this.modelSelect.hidden = true;
      const label = this.modelSelect.closest('.ricochet-search-machine__field')?.querySelector('label');
      if (label) label.htmlFor = 'ricochet-search-model-trigger';

      const selectedGroup = this.catalogVehicleMap.get(this.selectedVehicle);
      const trigger = document.createElement('button');
      trigger.id = 'ricochet-search-model-trigger';
      trigger.type = 'button';
      trigger.className = 'ricochet-search-model-trigger';
      trigger.dataset.modelPickerTrigger = '';
      trigger.dataset.machinePickerTrigger = '';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', 'ricochet-search-model-panel');
      trigger.disabled = !this.selectedMake;
      trigger.textContent = selectedGroup
        ? `${selectedGroup.model} — ${selectedGroup.matches.length} ${selectedGroup.matches.length === 1 ? 'part' : 'parts'}`
        : 'Choose a model';

      const panel = document.createElement('div');
      panel.id = 'ricochet-search-model-panel';
      panel.className = 'ricochet-search-model-panel';
      panel.dataset.modelPickerPanel = '';
      panel.dataset.machinePickerPanel = '';
      panel.hidden = true;
      const search = document.createElement('input');
      search.className = 'ricochet-search-model-panel__search';
      search.type = 'search';
      search.placeholder = 'Search models';
      search.setAttribute('aria-label', 'Search models');
      const list = document.createElement('div');
      list.className = 'ricochet-search-model-panel__list';
      list.setAttribute('role', 'listbox');
      list.setAttribute('aria-label', `${this.selectedMake || ''} models`.trim());
      if (suggested.length) list.append(this.createModelPickerSection('Suggested from your search', suggested));
      if (allModels.length) list.append(this.createModelPickerSection(`All ${this.selectedMake} models`, allModels, true));
      const empty = document.createElement('p');
      empty.className = 'ricochet-search-model-panel__empty';
      empty.textContent = 'No matching models. Try a shorter search.';
      empty.hidden = true;
      list.append(empty);
      panel.append(search, list);

      trigger.addEventListener('click', (event) => {
        event.stopPropagation();
        const opening = panel.hidden;
        this.closeMachinePickers();
        panel.hidden = !opening;
        trigger.setAttribute('aria-expanded', String(opening));
        if (opening) {
          this.fitMachinePickerToViewport(this.modelPickerElement);
          search.focus();
        }
      });
      panel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeMachinePickers();
          trigger.focus();
        } else if (event.key === 'ArrowDown' && event.target === search) {
          event.preventDefault();
          list.querySelector('[data-model-picker-option]:not([hidden])')?.focus();
        }
      });
      search.addEventListener('input', () => {
        const query = fold(search.value);
        list.querySelectorAll('[data-model-picker-option]').forEach((option) => {
          option.hidden = Boolean(query) && !fold(option.textContent).includes(query);
        });
        list.querySelectorAll('.ricochet-search-model-family').forEach((family) => {
          family.hidden = ![...family.querySelectorAll('[data-model-picker-option]')].some((option) => !option.hidden);
        });
        list.querySelectorAll('.ricochet-search-model-section').forEach((section) => {
          section.hidden = ![...section.querySelectorAll('[data-model-picker-option]')].some((option) => !option.hidden);
        });
        empty.hidden = [...list.querySelectorAll('[data-model-picker-option]')].some((option) => !option.hidden);
      });
      this.modelPickerElement.replaceChildren(trigger, panel);
    }

    addEventListeners() {
      this.typeSelect?.addEventListener('change', () => {
        this.selectionWasRequested = true;
        this.selectedType = this.typeSelect.value;
        const availableMakes = new Set(
          this.catalogVehicles.filter((group) => group.typeKey === this.selectedType).map((group) => group.make)
        );
        if (!availableMakes.has(this.selectedMake)) this.selectedMake = '';
        this.selectedVehicle = this.suggestedVehicles.find((group) => (
          group.typeKey === this.selectedType && group.make === this.selectedMake
        ))?.key || '';
        this.visibleCount = this.pageSize;
        this.populateMachineSelectors();
        this.updateUrl();
        this.render();
      });
      this.makeSelect?.addEventListener('change', () => {
        this.selectionWasRequested = true;
        this.selectedMake = this.makeSelect.value;
        this.selectedVehicle = this.suggestedVehicles.find((group) => (
          group.typeKey === this.selectedType && group.make === this.selectedMake
        ))?.key || '';
        this.visibleCount = this.pageSize;
        this.populateMachineSelectors();
        this.updateUrl();
        this.render();
      });
      this.modelSelect?.addEventListener('change', () => {
        this.selectionWasRequested = true;
        this.selectedVehicle = this.modelSelect.value;
        this.visibleCount = this.pageSize;
        this.populateMachineSelectors();
        this.updateUrl();
        this.render();
      });
      this.handleOutsideModelPickerClick = (event) => {
        if (!this.machinePickerElements().some((pickerElement) => pickerElement.contains(event.target))) {
          this.closeMachinePickers();
        }
      };
      this.handleModelPickerViewportChange = () => {
        this.machinePickerElements().forEach((pickerElement) => this.fitMachinePickerToViewport(pickerElement));
      };
      document.addEventListener('click', this.handleOutsideModelPickerClick);
      window.addEventListener('resize', this.handleModelPickerViewportChange);
      window.addEventListener('scroll', this.handleModelPickerViewportChange, { passive: true });
      this.showMoreButton?.addEventListener('click', () => {
        this.visibleCount += this.pageSize;
        this.render();
      });
      this.standardButton?.addEventListener('click', () => {
        this.hidden = true;
        const nativeResults = document.querySelector('[data-native-search-results]');
        if (nativeResults) nativeResults.hidden = false;
      });
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleOutsideModelPickerClick);
      window.removeEventListener('resize', this.handleModelPickerViewportChange);
      window.removeEventListener('scroll', this.handleModelPickerViewportChange);
      this.closest('.container')?.classList.remove('ricochet-search-progressive--selected');
    }

    updateUrl() {
      const url = new URL(window.location.href);
      ['fitment_model', 'fitment_product_type', 'fitment_material'].forEach((parameter) => url.searchParams.delete(parameter));
      if (this.vehicleMode && this.selectedType) url.searchParams.set('fitment_vehicle_type', this.selectedType);
      else url.searchParams.delete('fitment_vehicle_type');
      if (this.vehicleMode && this.selectedMake) url.searchParams.set('fitment_make', this.selectedMake);
      else url.searchParams.delete('fitment_make');
      if (this.vehicleMode && this.selectedVehicle) url.searchParams.set('fitment_vehicle', this.selectedVehicle);
      else url.searchParams.delete('fitment_vehicle');
      url.searchParams.delete('page');
      window.history.replaceState({}, '', url);
    }

    currentMatches() {
      if (!this.vehicleMode) return this.matches;
      if (!this.selectedVehicle) return this.matches;
      const group = this.catalogVehicleMap.get(this.selectedVehicle);
      if (!group) return [];
      return group.matches
        .map(({ document }) => ({ document, confidence: scoreDocument(document, this.query) }))
        .sort((left, right) => (
          right.confidence - left.confidence
          || Number(Boolean(right.document.a)) - Number(Boolean(left.document.a))
          || String(left.document.t).localeCompare(String(right.document.t))
        ));
    }

    createMetadata(product) {
      const metadata = [
        ['Product type', product.pt],
        ['Material', product.mt],
      ].filter(([, value]) => value);
      const list = document.createElement('dl');
      list.className = 'ricochet-search-result__metadata';
      metadata.forEach(([label, value]) => {
        const item = document.createElement('div');
        const term = document.createElement('dt');
        const description = document.createElement('dd');
        term.textContent = label;
        description.textContent = value;
        item.append(term, description);
        list.append(item);
      });
      return list;
    }

    createResult(match) {
      const { document: product, confidence } = match;
      const item = document.createElement('li');
      item.className = 'ricochet-search-result';
      const article = document.createElement('article');
      article.className = 'ricochet-search-result__card';
      article.dataset.searchResultCard = '';
      const productUrl = `/products/${encodeURIComponent(product.h)}`;

      const mediaLink = document.createElement('a');
      mediaLink.className = 'ricochet-search-result__media';
      mediaLink.href = productUrl;
      mediaLink.setAttribute('aria-label', `View ${product.t}`);
      if (product.im) {
        const image = document.createElement('img');
        image.className = 'ricochet-search-result__image';
        try {
          const imageUrl = new URL(product.im);
          imageUrl.searchParams.set('width', '360');
          image.src = imageUrl.toString();
        } catch (_error) {
          image.src = product.im;
        }
        image.alt = product.ia || product.t;
        image.loading = 'lazy';
        image.decoding = 'async';
        image.width = 240;
        image.height = 240;
        mediaLink.append(image);
      } else {
        const placeholder = document.createElement('span');
        placeholder.className = 'ricochet-search-result__image-placeholder';
        placeholder.textContent = 'Image unavailable';
        mediaLink.append(placeholder);
      }

      const content = document.createElement('div');
      content.className = 'ricochet-search-result__content';
      const eyebrow = document.createElement('p');
      eyebrow.className = 'ricochet-search-result__eyebrow';
      eyebrow.textContent = [product.mk, product.md].filter(Boolean).join(' · ');

      const headingRow = document.createElement('div');
      headingRow.className = 'ricochet-search-result__heading';
      const heading = document.createElement('h3');
      heading.className = 'h5';
      const link = document.createElement('a');
      link.href = productUrl;
      link.textContent = this.vehicleMode ? partTitle(product) : product.t;
      heading.append(link);
      headingRow.append(heading);
      if (!this.vehicleMode) {
        const confidenceElement = document.createElement('span');
        confidenceElement.className = `ricochet-search-result__confidence confidence-${Math.floor(confidence / 10)}`;
        confidenceElement.textContent = confidence === 100 ? 'Exact match' : (confidence >= 95 ? 'Best match' : 'Related match');
        headingRow.append(confidenceElement);
      }

      if (!this.vehicleMode && eyebrow.textContent) content.append(eyebrow);
      content.append(headingRow);
      if (product.d) {
        const description = document.createElement('p');
        description.className = 'ricochet-search-result__description';
        description.textContent = product.d;
        content.append(description);
      }
      content.append(this.createMetadata(product));

      const commerce = document.createElement('p');
      commerce.className = 'ricochet-search-result__commerce';
      if (product.p) {
        const price = new Intl.NumberFormat(undefined, { style: 'currency', currency: this.currency }).format(Number(product.p));
        commerce.textContent = `From ${price}`;
      }
      if (product.a) commerce.append(document.createTextNode(`${commerce.textContent ? ' · ' : ''}Available`));
      if (commerce.textContent) content.append(commerce);

      article.append(mediaLink, content);
      item.append(article);
      return item;
    }

    render() {
      const matches = this.currentMatches();
      const visible = matches.slice(0, this.visibleCount);
      const list = document.createElement('ol');
      list.className = 'ricochet-search-results-list';
      list.append(...visible.map((match) => this.createResult(match)));
      const selectedGroup = this.vehicleMode ? this.catalogVehicleMap.get(this.selectedVehicle) : null;
      this.classList.toggle('is-machine-selected', Boolean(selectedGroup));
      this.closest('.container')?.classList.toggle('ricochet-search-progressive--selected', Boolean(selectedGroup));

      if (this.vehicleMode) {
        if (selectedGroup) {
          const header = document.createElement('div');
          header.className = 'ricochet-search-selected-vehicle';
          const label = document.createElement('p');
          label.className = 'ricochet-search-selected-vehicle__label';
          label.textContent = 'Parts that fit';
          const heading = document.createElement('h2');
          heading.textContent = selectedGroup.label;
          const count = document.createElement('p');
          count.textContent = `${matches.length.toLocaleString()} compatible ${matches.length === 1 ? 'part' : 'parts'}`;
          header.append(label, heading, count);
          this.resultsElement.replaceChildren(header, list);
          this.statusElement.textContent = this.selectionWasRequested
            ? 'Your selected machine is shown below. Change the make or model at any time.'
            : `We prefilled the closest machine for “${this.query}”. Change the make or model if it isn't right.`;
        } else {
          const closeModels = this.suggestedVehicles.filter((group) => (
            group.typeKey === this.selectedType && group.make === this.selectedMake
          ));
          this.resultsElement.replaceChildren(list);
          this.statusElement.textContent = closeModels.length > 1
            ? `We found several close models for “${this.query}”. Choose yours above to narrow these ${matches.length.toLocaleString()} search results to exact-fit parts.`
            : `${matches.length.toLocaleString()} search ${matches.length === 1 ? 'result' : 'results'} for “${this.query}”. Choose your exact machine above to narrow ${matches.length === 1 ? 'it' : 'them'} to confirmed-fit parts.`;
        }
      } else {
        this.resultsElement.replaceChildren(list);
        this.statusElement.textContent = matches.length
          ? `${matches.length.toLocaleString()} product ${matches.length === 1 ? 'result' : 'results'} for “${this.query}”.`
          : `No product results found for “${this.query}”.`;
      }

      if (this.normalizedElement) {
        const changed = fold(this.normalizedQuery) !== fold(this.query);
        this.normalizedElement.hidden = this.vehicleMode || !changed;
        this.normalizedElement.textContent = !this.vehicleMode && changed ? `Showing results for “${this.normalizedQuery}”.` : '';
      }
      if (this.showMoreButton) {
        this.showMoreButton.hidden = visible.length >= matches.length;
        this.showMoreButton.textContent = `Show more (${matches.length - visible.length} remaining)`;
      }
    }
  }

  customElements.define('ricochet-search-results', RicochetSearchResults);
})();
